import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { SHOP_SECTION_ID } from '../../utils/scroll';
import { buttonClasses } from '../../utils/buttonStyles';
export const STICKY_BAR_ROUTES = ['/', '/m1-pro', '/magnetic-mount', '/engineering', '/applications', '/promise'];

/**
 * Mobile/tablet sticky purchase bar — always reflects the currently selected system.
 * On the homepage it hides while the hero or the shop section is on screen.
 */
export function StickyBuyBar() {
  const {
    t,
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    pathname
  } = useLocation();
  const {
    startSystem
  } = useFunnel();
  const {
    selectedId
  } = useSystemSelection();
  const enabled = STICKY_BAR_ROUTES.includes(pathname);
  const [visible, setVisible] = useState(false);
  const bundle = getBundle(selectedId);
  const p = priceBundle(bundle);
  useEffect(() => {
    setVisible(false);
    if (!enabled) return;
    if (pathname === '/') {
      const hero = document.getElementById('home-hero');
      const shop = document.getElementById(SHOP_SECTION_ID);
      if (!hero || !shop) return;
      const state = {
        hero: true,
        shop: false
      };
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.target === hero) state.hero = e.isIntersecting;
          if (e.target === shop) state.shop = e.isIntersecting;
        });
        setVisible(!state.hero && !state.shop);
      });
      io.observe(hero);
      io.observe(shop);
      return () => io.disconnect();
    }
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname, enabled]);
  if (!enabled) return null;
  return <AnimatePresence data-id="element-976" data-mp="2nkob50j7xodd">
      {visible && <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-x-0 bottom-0 z-30 border-t border-marine/30 bg-deep/95 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md lg:hidden" data-id="element-977" data-mp="2shohlcjzct50">
          <div className="mx-auto flex max-w-site items-center justify-between gap-3" data-id="element-978" data-mp="28tywtpagnt5a">
            <div className="min-w-0 leading-tight" data-id="element-979" data-mp="28rumaoi23vw1">
              <p className="truncate font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-white" data-id="element-980" data-mp="2dlpao58lwz1g">
                {es ? bundle.taglineEs : bundle.tagline} — {bundle.qty} {bundle.qty === 1 ? es ? 'luz' : 'light' : es ? 'luces' : 'lights'}
              </p>
              <p className="text-[13px] text-titanium" data-id="element-981" data-mp="1dldg71q6twgk">
                <span className="font-semibold text-white" data-id="element-982" data-mp="1dk2agwd6memt">{formatMoney(p.total, lang)}</span>
                {p.savings > 0 && <span className="text-ice" data-id="element-983" data-mp="14jx48pjq5ma8">
                    {' '}
                    · {es ? 'Ahorra' : 'Save'} {formatMoney(p.savings, lang)}
                  </span>}
              </p>
            </div>
            <button type="button" onClick={() => startSystem(bundle.id)} className={`${buttonClasses('primary')} !h-11 shrink-0 !px-5`} data-id="element-984" data-mp="um9d09lbuwwt">
              {t('common.addToCart')}
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}